import { Button, Dropdown, Form } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import { usePayeurSearch, useProduitSearch } from '@/hooks';
import type { PayeurType } from '@/types';
import type { ProduitsListeItemType } from '@/types/produit';

import { CREER_PAYEUR, QUANTITE_MAX, QUANTITE_MIN, useQuantiteSaisie } from './comptant';

interface ComptantSaisieProps {
  payeur: PayeurType | null;
  produit: ProduitsListeItemType | null;
  /** `null` tant qu'aucun produit n'est sélectionné : le champ reste vide. */
  quantite: number | null;
  onPayeurChange: (payeur: PayeurType | null) => void;
  onCreatePayeur: () => void;
  onProduitChange: (produit: ProduitsListeItemType | null) => void;
  onQuantiteChange: (quantite: number) => void;
  onValider: () => void;
}

const ComptantSaisie = (props: ComptantSaisieProps) => {
  const {
    payeur,
    produit,
    quantite,
    onPayeurChange,
    onCreatePayeur,
    onProduitChange,
    onQuantiteChange,
    onValider,
  } = props;

  const {
    loading: loadingPayeurs,
    options: payeurOptions,
    payeurs,
    onSearchChange: onSearchPayeur,
  } = usePayeurSearch();

  const {
    loading: loadingProduits,
    options: produitOptions,
    produits,
    onSearchChange: onSearchProduit,
  } = useProduitSearch();

  // La recherche est faite côté serveur ; on ajoute une option d'ajout, au libellé fixe, en fin de liste.
  const payeurDropdownOptions = React.useMemo(
    () => [
      ...payeurOptions,
      { key: CREER_PAYEUR, value: CREER_PAYEUR, text: 'Créer un nouveau payeur' },
    ],
    [payeurOptions],
  );

  const handleSelectPayeur = React.useCallback(
    (e: React.SyntheticEvent, { value }: { value: number | string }) => {
      if (value === CREER_PAYEUR) {
        onCreatePayeur();
        return;
      }
      onPayeurChange(_.find(payeurs, { identifiant: value as number }) ?? null);
    },
    [payeurs, onPayeurChange, onCreatePayeur],
  );

  const handleSelectProduit = React.useCallback(
    (e: React.SyntheticEvent, { value }: { value: number }) => {
      onProduitChange(_.find(produits, { identifiant: value }) ?? null);
    },
    [produits, onProduitChange],
  );

  const {
    raw: rawQuantite,
    handleChange: handleChangeQuantite,
    handleBlur: handleBlurQuantite,
  } = useQuantiteSaisie(quantite, onQuantiteChange);

  return (
    <Form.Group style={{ margin: 0 }}>
      <Dropdown
        selection
        selectOnBlur={false}
        search={(options) => options}
        data-testid="comptant-payeur"
        placeholder="Sélectionner un payeur"
        noResultsMessage="Rechercher un payeur..."
        loading={loadingPayeurs}
        options={payeurDropdownOptions}
        value={payeur?.identifiant ?? ''}
        onSearchChange={onSearchPayeur}
        onChange={handleSelectPayeur}
        style={{ width: 400 }}
      />
      <Dropdown
        selection
        selectOnBlur={false}
        search={(options) => options}
        data-testid="comptant-produit"
        placeholder="Sélectionner un produit"
        noResultsMessage="Rechercher un produit..."
        loading={loadingProduits}
        disabled={_.isNil(payeur)}
        options={produitOptions}
        value={produit?.identifiant ?? ''}
        onSearchChange={onSearchProduit}
        onChange={handleSelectProduit}
        style={{ marginLeft: '1em', width: 400 }}
      />
      <Form.Input
        data-testid="comptant-quantite"
        type="number"
        min={QUANTITE_MIN}
        max={QUANTITE_MAX}
        // La quantité ne devient saisissable qu'une fois le produit choisi.
        disabled={_.isNil(produit)}
        value={rawQuantite}
        style={{ width: 50 }}
        onChange={(e, { value }) => handleChangeQuantite(value)}
        onFocus={(e) => e.target.select()}
        onBlur={handleBlurQuantite}
      />
      <Button
        positive
        type="button"
        icon="check"
        data-testid="comptant-valider"
        disabled={_.isNil(payeur) || _.isNil(produit)}
        onClick={onValider}
        style={{ marginLeft: '1em' }}
      />
    </Form.Group>
  );
};

export default ComptantSaisie;
